import {MediaCardType, MediaString} from '@wandb/weave/common/types/media';
import {uuidv4} from '@wandb/weave/core/util/id';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useParams} from 'react-router-dom';

import {getDatadogRum} from '../../util/datadog';
import {
  useRampFlagMediaPanelTracking,
  useRampFlagMediaPanelTrackingDebug,
} from '../../util/rampFeatureFlags';

const supportedMediaTypes: MediaCardType[] = [
  'image',
  'video',
  'audio',
  'object3D',
  'plotly',
  'molecule',
  'html',
];
const DD_RUM_METRIC = 'mediaBrowserPanelTracking';
const DD_RUM_DESCRIPTION = 'Media browser panel load duration';

export type MediaBrowserPanelTrackingContextType = {
  state: MediaBrowserPanelTrackingContextState;
  isMediaBrowserPanelTrackingEnabled: boolean;
  isMediaBrowserPanelTrackingDebugEnabled: boolean;
  isMediaBrowserPanelTrackingSupported: boolean;
  incrementCompleted: () => void;
};

export type MediaBrowserPanelTrackingContextState = {
  panelID: string;
  panelLoadUUID: string;
  mediaType?: MediaCardType | null;
  mediaString?: MediaString | null;
  endTimeMs?: number;
  startTimeMs?: number;
  tileCount: number;
};

// Default values (can be empty or from initial props)
export const defaultState: MediaBrowserPanelTrackingContextState = {
  panelID: '',
  panelLoadUUID: '',
  mediaType: null,
  mediaString: null,
  endTimeMs: 0,
  startTimeMs: 0,
  tileCount: 0,
};

export const defaultContext: MediaBrowserPanelTrackingContextType = {
  state: defaultState,
  isMediaBrowserPanelTrackingEnabled: false,
  isMediaBrowserPanelTrackingDebugEnabled: false,
  isMediaBrowserPanelTrackingSupported: false,
  incrementCompleted: () => {},
};

export const MediaBrowserPanelTrackingContext =
  createContext<MediaBrowserPanelTrackingContextType>({
    state: defaultState,
    isMediaBrowserPanelTrackingEnabled: false,
    isMediaBrowserPanelTrackingDebugEnabled: false,
    isMediaBrowserPanelTrackingSupported: false,
    incrementCompleted: () => {},
  });

export const useMediaBrowserPanelTrackingContext = () => {
  const context = useContext(MediaBrowserPanelTrackingContext);
  if (!context) {
    throw new Error(
      'useMediaBrowserPanelTrackingContext must be used within a MediaBrowserPanelTrackingContextProvider'
    );
  }
  return context;
};

export const useMediaBrowserPanelTrackingContextMaybe = () => {
  const context = useContext(MediaBrowserPanelTrackingContext);
  if (!context) {
    return defaultContext;
  }
  return context;
};

export type MediaBrowserPanelTrackingContextProviderProps = {
  children: React.ReactNode;
  panelId?: string;
  mediaType?: MediaCardType | null;
  mediaString?: MediaString | null;
  tileCount?: number;
};

export const MediaBrowserPanelTrackingContextProvider = React.memo(
  ({
    mediaType,
    mediaString,
    panelId,
    tileCount,
    children,
  }: MediaBrowserPanelTrackingContextProviderProps) => {
    const {entityName} = useParams<{entityName: string}>();
    const isMediaPanelTrackingEnabled =
      useRampFlagMediaPanelTracking(entityName);
    const isDebugMediaPanelTrackingEnabled =
      useRampFlagMediaPanelTrackingDebug(entityName);
    const isMediaTypeSupported =
      mediaType && supportedMediaTypes.includes(mediaType);
    const [hasEmitted, setHasEmitted] = useState(false);
    const isPanelTrackingEnabled =
      !!isMediaPanelTrackingEnabled && !!isMediaTypeSupported && !hasEmitted;

    // Move all mutable state into a single ref
    const stateRef = useRef({
      state: {
        ...defaultState,
        panelLoadUUID: uuidv4(),
        startTimeMs: Date.now(),
        panelID: panelId ?? '',
        mediaType,
        mediaString,
        tileCount: tileCount ?? 0,
      },
      completed: 0,
    });
    // Keep state.current in sync with prop changes for dynamic fields
    React.useEffect(() => {
      stateRef.current.state.mediaType = mediaType;
      stateRef.current.state.mediaString = mediaString;
      stateRef.current.state.tileCount = tileCount ?? 0;
      stateRef.current.state.panelID = panelId ?? '';
    }, [mediaType, mediaString, tileCount, panelId]);

    const loggingPrefix = useRef(
      `MediaBrowserPanelTracking ${panelId} panel ${mediaType}`
    );

    const log = useCallback(
      (event: string, msg: any) => {
        if (isDebugMediaPanelTrackingEnabled) {
          console.log(loggingPrefix.current, event, JSON.stringify(msg));
        }
      },
      [isDebugMediaPanelTrackingEnabled]
    );

    const getDuration = useCallback(() => {
      if (
        stateRef.current.state.startTimeMs == null ||
        stateRef.current.state.endTimeMs == null
      ) {
        return 0;
      }
      return (
        stateRef.current.state.endTimeMs - stateRef.current.state.startTimeMs
      );
    }, []);

    // Memoize the vital data calculation
    const getVital = useCallback(() => {
      return {
        startTime: stateRef.current.state.startTimeMs,
        description: DD_RUM_DESCRIPTION,
        duration: getDuration(),
        context: stateRef.current.state,
      };
    }, [getDuration]);

    // Memoize the emit function
    const emit = useCallback(() => {
      if (!isPanelTrackingEnabled) {
        return;
      }
      setHasEmitted(true);

      const ddContext = getVital();
      try {
        const dd_rum = getDatadogRum();
        dd_rum.addDurationVital(DD_RUM_METRIC, ddContext);
      } catch (error) {
        console.error('Error adding duration vital', error);
      } finally {
        if (isDebugMediaPanelTrackingEnabled) {
          log('emit', ddContext);
        }
      }
    }, [
      isPanelTrackingEnabled,
      isDebugMediaPanelTrackingEnabled,
      log,
      getVital,
    ]);

    // Memoize the increment function with stable dependencies
    const incrementCompleted = useCallback(() => {
      stateRef.current.completed++;
      if (
        stateRef.current.state.tileCount > 0 &&
        stateRef.current.completed === stateRef.current.state.tileCount
      ) {
        stateRef.current.state.endTimeMs = Date.now();
        emit();
      }
    }, [emit]);

    const contextValue = useMemo(() => {
      // Only expose tracking as enabled and the real incrementCompleted
      // if panel tracking is currently enabled (not emitted, supported type, etc)
      return {
        isMediaBrowserPanelTrackingEnabled: isPanelTrackingEnabled,
        isMediaBrowserPanelTrackingDebugEnabled:
          !!isDebugMediaPanelTrackingEnabled,
        isMediaBrowserPanelTrackingSupported: !!isMediaTypeSupported,
        state: stateRef.current.state,
        incrementCompleted: isPanelTrackingEnabled
          ? incrementCompleted
          : () => {},
      };
    }, [
      incrementCompleted,
      isPanelTrackingEnabled,
      isDebugMediaPanelTrackingEnabled,
      isMediaTypeSupported,
    ]);

    return (
      <MediaBrowserPanelTrackingContext.Provider value={contextValue}>
        {children}
      </MediaBrowserPanelTrackingContext.Provider>
    );
  }
);
